import { defineStore } from 'pinia'

interface ModalInstance {
  id: string
  historyState?: any
}

export const useModalWindowStore = defineStore('modal_window', {
  state: () => ({
    openModals: new Set<string>(),
    modalStack: [] as ModalInstance[]
  }),

  getters: {
    // Backwards compatible with existing code (e.g., ProductPageType.vue)
    isOpen: state => state.openModals.size > 0,

    // Number of currently open modals
    openCount: state => state.openModals.size,

    // Check if this is the first modal being opened
    isFirstModal: state => state.openModals.size === 1,

    // Check if no modals are open (after closing)
    isLastModal: state => state.openModals.size === 0
  },

  actions: {
    /**
     * Register a modal instance when it opens
     * @param id - Unique modal identifier
     * @param historyState - Optional history state for browser navigation
     */
    registerModal (id: string, historyState?: any) {
      this.openModals.add(id)
      this.modalStack.push({ id, historyState })
    },

    /**
     * Unregister a modal instance when it closes
     * @param id - Unique modal identifier
     */
    unregisterModal (id: string) {
      this.openModals.delete(id)
      const index = this.modalStack.findIndex(m => m.id === id)
      if (index !== -1) {
        this.modalStack.splice(index, 1)
      }
    },

    /**
     * Check if a specific modal is currently registered
     * @param id - Unique modal identifier
     * @returns true if modal is in the open set
     */
    hasModal (id: string): boolean {
      return this.openModals.has(id)
    },

    /**
     * Get the topmost modal in the stack
     * Used for popstate handling to close modals one-by-one
     * @returns The most recently opened modal or undefined
     */
    getTopModal (): ModalInstance | undefined {
      return this.modalStack[this.modalStack.length - 1]
    }
  }
})
